import type { ReactNode } from "react";

// =============================================================================
// Quick Action Types
// =============================================================================

/**
 * A quick action that can be triggered from blockchain components
 * to continue the conversation with pre-populated prompts
 */
export type QuickAction = {
  /** Display label for the action button */
  label: string;
  /** Optional icon to display */
  icon?: ReactNode;
  /** The prompt text to populate in the chat input */
  prompt: string;
  /** If true, sends the message immediately instead of just populating */
  autoSend?: boolean;
  /** If true, hides this action (for conditional rendering) */
  hidden?: boolean;
};

/**
 * Callback type for handling quick actions
 */
export type QuickActionHandler = (prompt: string, autoSend?: boolean) => void;

// =============================================================================
// Chain Configuration Types
// =============================================================================

export type ChainId = "1" | "137" | "8453" | "10" | "42161";

export type ChainInfo = {
  id: ChainId;
  name: string;
  shortName: string;
  color: string;
  bgGradient: string;
  explorerUrl: string;
  nativeCurrency: {
    symbol: string;
    decimals: number;
  };
};

export const CHAIN_INFO: Record<ChainId, ChainInfo> = {
  "1": {
    id: "1",
    name: "Ethereum",
    shortName: "ETH",
    color: "#627eea",
    bgGradient: "from-[#627eea]/10 to-[#627eea]/5",
    explorerUrl: "https://eth.blockscout.com",
    nativeCurrency: { symbol: "ETH", decimals: 18 },
  },
  "137": {
    id: "137",
    name: "Polygon",
    shortName: "MATIC",
    color: "#8247e5",
    bgGradient: "from-[#8247e5]/10 to-[#8247e5]/5",
    explorerUrl: "https://polygon.blockscout.com",
    nativeCurrency: { symbol: "MATIC", decimals: 18 },
  },
  "8453": {
    id: "8453",
    name: "Base",
    shortName: "BASE",
    color: "#0052ff",
    bgGradient: "from-[#0052ff]/10 to-[#0052ff]/5",
    explorerUrl: "https://base.blockscout.com",
    nativeCurrency: { symbol: "ETH", decimals: 18 },
  },
  "10": {
    id: "10",
    name: "Optimism",
    shortName: "OP",
    color: "#ff0420",
    bgGradient: "from-[#ff0420]/10 to-[#ff0420]/5",
    explorerUrl: "https://optimism.blockscout.com",
    nativeCurrency: { symbol: "ETH", decimals: 18 },
  },
  "42161": {
    id: "42161",
    name: "Arbitrum",
    shortName: "ARB",
    color: "#28a0f0",
    bgGradient: "from-[#28a0f0]/10 to-[#28a0f0]/5",
    explorerUrl: "https://arbitrum.blockscout.com",
    nativeCurrency: { symbol: "ETH", decimals: 18 },
  },
};

export function getChainInfo(chainId: string): ChainInfo {
  const chain = CHAIN_INFO[chainId as ChainId];
  if (chain) {
    return chain;
  }
  // Fallback for unknown chains
  return {
    id: chainId as ChainId,
    name: `Chain ${chainId}`,
    shortName: chainId,
    color: "#888888",
    bgGradient: "from-gray-500/10 to-gray-500/5",
    explorerUrl: "",
    nativeCurrency: { symbol: "ETH", decimals: 18 },
  };
}

// =============================================================================
// Transaction Types
// =============================================================================

export type TransactionDirection = "incoming" | "outgoing" | "self";

export type TransactionStatus = "success" | "failed" | "pending";

export type DecodedLog = {
  name: string;
  address: string;
  topics: string[];
  data: string;
  decoded?: {
    eventName: string;
    args: Record<string, string>;
  };
};

export type TransactionSummary = {
  hash: string;
  chainId: string;
  from: string;
  to: string | null;
  value: string;
  gasUsed: string;
  gasPrice: string;
  timestamp: string;
  status: TransactionStatus;
  method?: string;
  blockNumber: string;
};

export type TransactionDetails = TransactionSummary & {
  logs: DecodedLog[];
  input: string;
  nonce: number;
  contractCreated?: string;
  tokenTransfers?: TokenTransfer[];
};

// =============================================================================
// Token Types
// =============================================================================

export type TokenType = "ERC20" | "ERC721" | "ERC1155" | "NATIVE" | "UNKNOWN";

export type TokenTransfer = {
  token: {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    type: TokenType;
  };
  from: string;
  to: string;
  value: string;
  tokenId?: string;
};

export type TokenBalance = {
  token: {
    address: string;
    symbol: string;
    name: string;
    decimals: number;
    type: TokenType;
    iconUrl?: string;
  };
  balance: string;
  usdValue?: string;
};

export type TokenInfo = {
  address: string;
  type: TokenType;
  name: string;
  symbol: string;
  decimals?: number;
  totalSupply?: string;
  holdersCount?: string;
  exchangeRate?: string;
  iconUrl?: string;
  description?: string;
};

export type TokenHolder = {
  address: string;
  balance: string;
  percentage?: number;
  isContract?: boolean;
  name?: string;
};

// =============================================================================
// Wallet Types
// =============================================================================

export type WalletState = {
  address: string;
  chainId: string;
  tokens: TokenBalance[];
  recentTransfers: TokenTransfer[];
  nativeBalance?: string;
  totalUsdValue?: string;
};

// =============================================================================
// Event Log Types
// =============================================================================

/**
 * A single event log entry from a smart contract
 */
export type EventLogEntry = {
  /** The contract address that emitted the log */
  address: string;
  /** Array of topics (topic0 is event signature, topic1-3 are indexed params) */
  topics: string[];
  /** Non-indexed event data (hex encoded) */
  data: string;
  /** Block number where the event was emitted */
  blockNumber: string;
  /** Unix timestamp of the block */
  timestamp: string;
  /** Transaction hash that emitted this event */
  transactionHash: string;
  /** Log index within the transaction */
  logIndex: string;
};

/**
 * Query parameters used to fetch event logs
 */
export type EventLogQueryParams = {
  address?: string;
  topic0?: string;
  topic1?: string;
  topic2?: string;
  topic3?: string;
};

/**
 * Output from the getEventLogs tool
 */
export type EventLogsOutput = {
  logs: EventLogEntry[];
  count: number;
  blockRange: {
    from: string;
    to: string;
  };
  queryParams: EventLogQueryParams;
  message?: string;
  truncated?: boolean;
};

// =============================================================================
// Tool State Types (for streaming)
// =============================================================================

export type ToolPartState =
  | "input-streaming"
  | "input-available"
  | "output-available"
  | "output-error";

// =============================================================================
// Component Props Types
// =============================================================================

export type BlockchainComponentBaseProps = {
  /** Unique identifier for this card (used for collapse state) */
  cardId: string;
  /** Handler for quick actions to populate chat input */
  onQuickAction: QuickActionHandler;
  /** Current state of the tool execution */
  state: ToolPartState;
  /** Whether the component is in a readonly context */
  isReadonly?: boolean;
};

/**
 * Enhanced error format that includes suggestions from AIToolError
 */
export type EnhancedError = {
  error: string;
  suggestions?: string[];
  context?: Record<string, unknown>;
};

export type TransactionDetailsProps = BlockchainComponentBaseProps & {
  input: {
    transactions: string[];
    chainId: string;
  };
  output?: TransactionDetails[] | EnhancedError;
};

export type TransactionListProps = BlockchainComponentBaseProps & {
  input: {
    address: string;
    chainId: string;
    startTime: string;
    endTime: string;
    numTransactions?: number;
  };
  output?:
    | {
        transactions: TransactionSummary[];
        numTransactionsFound?: number;
        timeRange?: { start: string; end: string };
        message?: string;
      }
    | EnhancedError;
};

export type WalletOverviewProps = BlockchainComponentBaseProps & {
  input: {
    walletAddresses: string[];
    chainId: string;
  };
  output?: WalletState[] | EnhancedError;
};

export type TokenCardProps = BlockchainComponentBaseProps & {
  input: {
    tokens: string[];
    chainId: string;
  };
  output?:
    | Record<string, TokenInfo & { holders?: TokenHolder[]; error?: string }>
    | EnhancedError;
};

export type EventLogsProps = BlockchainComponentBaseProps & {
  input: {
    chainId: string;
    address?: string;
    fromBlock: string;
    toBlock: string;
    topic0?: string;
    topic1?: string;
    topic2?: string;
    topic3?: string;
    topic0_1_opr?: "and" | "or";
    topic0_2_opr?: "and" | "or";
    topic0_3_opr?: "and" | "or";
    topic1_2_opr?: "and" | "or";
    topic1_3_opr?: "and" | "or";
    topic2_3_opr?: "and" | "or";
  };
  output?: EventLogsOutput | EnhancedError;
};
